import React, { useEffect, useState } from 'react';

import { UseOutletContextAdmin } from './Admin';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { ApiDigitalAuthInvitation } from '../../../services/auth/invitation.service';
import { Invitation } from '../../../models/invitation.model';
import { GlobalSwal } from '../../../styles/swal.style';
import { modalShow } from '../../../utils/modal.util';
import { errorWebHandler } from '../../../scripts/error.web.handler.script';

import AppPreloader from '../../../components/Preloader';
import AppModalNotificationInvitation from '../../../components/modal/ModalInvitationCreate';

export interface InvitationListPageProps {};

const InvitationListPage: React.FunctionComponent<InvitationListPageProps> = props => {
  const {setRoute, appServiceForUser, companyForUser} = UseOutletContextAdmin()
  const {lang} = StateLanguage()

  const [mounted, setMounted] = useState(false)
  const [invitations, setInvitations] = useState<Invitation[] | null>(null)

  const loadInvitations = async (api_key: string) => {
    await ApiDigitalAuthInvitation.getCompanyInvitations(api_key).then((response) => {
      if (response.status === 200) {
        setInvitations(Array.isArray(response.data) ? response.data : [])
      } else {
        setInvitations([])
        errorWebHandler(lang, {response})
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
      setInvitations([])
    })
  }

  const handleRevoke = async (invitation_id: string) => {
    if (!appServiceForUser) return

    GlobalSwal.fire({
      title: lang.labels.confirmRevoke,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesDelete,
      cancelButtonText: lang.labels.noCancel
    }).then(async (result) => {
      if (result.isConfirmed) {
        await ApiDigitalAuthInvitation.deleteInvitation(appServiceForUser!.api_key, invitation_id).then((response) => {
          if (response.status === 200) {
            loadInvitations(appServiceForUser!.api_key)

            GlobalSwal.fire({
              text: lang.labels.actionCompletedReturningToPage,
              icon: 'success',
              showConfirmButton: false,
              timer: 2200
            })
          } else {
            errorWebHandler(lang, {response})
          }
        }).catch((error) => {
          errorWebHandler(lang, error)
        })
      }
    })
  }

  const handleInvite = () => {
    modalShow('modal-invitation-create')
  }

  function statusBadgeClass(status: string): string {
    switch (status) {
      case 'pending':  return 'badge-light-warning'
      case 'accepted': return 'badge-light-success'
      case 'rejected': return 'badge-light-danger'
      case 'expired':  return 'badge-light-secondary'
      default:         return 'badge-light'
    }
  }

  function statusLabel(status: string): string {
    switch (status) {
      case 'pending':  return lang.labels.statusPending
      case 'accepted': return lang.labels.statusAccepted
      case 'rejected': return lang.labels.statusRejected
      case 'expired':  return lang.labels.statusExpired
      default:         return status
    }
  }

  function formatDate(dateStr: string): string {
    if (!dateStr) return '-'
    try {
      return new Date(dateStr).toLocaleDateString()
    } catch {
      return dateStr
    }
  }

  useEffect(() => {
    if (appServiceForUser) {
      setInvitations(null)
      loadInvitations(appServiceForUser.api_key)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.administration, branch: lang.labels.invitations}, company: true})

    return () => setMounted(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { invitations !== null
        ?
        <div className="card card-flush">
          <div className="card-header">
            <div className="card-title">
              <h3 className="fw-bolder">{lang.labels.invitations}</h3>
            </div>
            <div className="card-toolbar">
              <button className="btn btn-primary" type="button" onClick={handleInvite}>
                <div className="d-flex align-items-center">
                  <span className="svg-icon svg-icon-2 ms-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect opacity="0.5" x="11.364" y="20.364" width="16" height="2" rx="1" transform="rotate(-90 11.364 20.364)" fill="black" />
                      <rect x="4.36396" y="11.364" width="16" height="2" rx="1" fill="black" />
                    </svg>
                  </span>
                  {lang.labels.invite}
                </div>
              </button>
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="min-w-200px sorting">{lang.labels.email}</th>
                      <th className="min-w-100px sorting">{lang.labels.status}</th>
                      <th className="min-w-150px sorting">{lang.labels.roles}</th>
                      <th className="min-w-100px sorting">{lang.labels.sentAt}</th>
                      <th className="min-w-100px sorting">{lang.labels.expiresAt}</th>
                      <th className="w-75px sorting_disabled text-end">{lang.labels.actions}</th>
                    </tr>
                  </thead>
                  { invitations.length > 0
                    ?
                    <tbody className="text-secondary fw-bold">
                      { invitations.map((item, index) => (
                        <tr key={index} className="row-dashed">
                          <td>{item.email}</td>
                          <td>
                            <span className={`badge ${statusBadgeClass(item.status)} fs-8`}>
                              {statusLabel(item.status)}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              {item.role_ids && item.role_ids.length > 0
                                ? item.role_ids.map((rid, ri) => (
                                  <span key={ri} className="badge badge-light fs-8">{typeof rid === 'object' ? rid.name : rid}</span>
                                ))
                                : <small className="text-muted">{lang.labels.none}</small>
                              }
                            </div>
                          </td>
                          <td>{formatDate(item.created_at)}</td>
                          <td>{formatDate(item.expires_at)}</td>
                          <td>
                            <div className="d-flex justify-content-end">
                              { item.status === 'pending' &&
                                <button className="btn btn-sm btn-light-danger" type="button" onClick={() => handleRevoke(item.id)}>
                                  {lang.labels.revoke}
                                </button>
                              }
                              { (item.status === 'expired' || item.status === 'rejected') &&
                                <button className="btn btn-sm btn-light-primary" type="button" onClick={handleInvite}>
                                  {lang.labels.reinvite}
                                </button>
                              }
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    :
                    <tbody>
                      <tr className="text-secondary fw-bold">
                        <td colSpan={6} className="text-center">{lang.labels.noInvitations}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalNotificationInvitation appServiceForUser={appServiceForUser} companyForUser={companyForUser}></AppModalNotificationInvitation>
    </div>
  )
};

export default InvitationListPage;
